import { Grid } from '@material-ui/core';
import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { CloseButton } from 'src/close-button';
import BaseButton from 'src/common/components/base-button';
import { pushEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import IAlertCategory from 'src/common/interfaces/states/IAlertCategory';
import { AppState } from 'src/common/redux/configure-store';
import { AlertActions } from 'src/common/redux/reducers/alert-reducer';
import DialogTitle from 'src/dialog-title/dialog-title';
import Pagination from 'src/pagination/pagination';
import SizedGridItem from 'src/sized-grid-item/sized-grid-item';
import { styles } from './alerts-styles';
import AlertMenuCategoryButton from './category-button';
import EmeraldDialog from '../../common/components/emerald-dialog/emerald-dialog';
import { useScrollButtonState } from '@ncr-retail/hooks';

type Props = IAlertsProps & IAlertsState & typeof mapDispatchToProps;

export const showAlertDialog = (props: IAlertsState): void => {
  pushEmeraldModal(<AlertsModal {...props} />);
};

const Alerts: React.FC<Props> = ({
  alertCategories,
  onAlertClick,
  onClose,
  onCategoryClick,
}): JSX.Element => {
  const classes = styles();

  const AlertMenu = ({
    categories,
    clickHandler,
    closeClickHandler,
    clickAlertHandler,
  }): JSX.Element | null => {
    const MAX_ALERTS_PER_PAGE = 8;

    const selectedCategory = categories.find((cat): boolean => {
      return cat.selected;
    });

    const totalPages = selectedCategory
      ? Math.ceil(selectedCategory.alerts.length / MAX_ALERTS_PER_PAGE)
      : 0;

    const [
      { currentPage: currentScrollPage, lastPage: lastScrollPage, onPageChanged },
      scrollRef,
    ] = useScrollButtonState();
    const [currentPage, setCurrentPage] = useState(totalPages === 0 ? 0 : 1);
    const handlePageChange = useCallback(
      (page: number) => {
        setCurrentPage(page);
      },
      [setCurrentPage],
    );

    if (!selectedCategory) {
      return null;
    }

    return (
      <Grid container className={classes.alertMenuContainer} style={{ flex: '1 1 0' }}>
        {/* level 1, left side */}
        <SizedGridItem size={'15.55vw'} className={`${classes.alertMenuLeft}`}>
          <Grid
            container
            direction='column'
            justifyContent='space-between'
            className={classes.alertMenuFullHeight}
          >
            <div
              ref={scrollRef}
              className={`verticalScroll fullWidth ${classes.customContainerHeight}`}
            >
              <Grid item>
                {/* Category Buttons */}
                {categories.map(
                  (category, index): JSX.Element => {
                    return (
                      <AlertMenuCategoryButton
                        category={category}
                        clickHandler={clickHandler}
                        key={index}
                      />
                    );
                  },
                )}
              </Grid>
            </div>
            <Grid
              item
              container
              justifyContent='space-evenly'
              className={classes.alertMenuLeftBottom}
            >
              {/* Bottom Left */}
              <Pagination
                currentPage={currentScrollPage}
                totalPages={lastScrollPage}
                updateCurrentPageHandler={onPageChanged}
                displayText={false}
                testIdPrefix={'alerts-bottom-left'}
              />
            </Grid>
          </Grid>
        </SizedGridItem>

        {/* level 2, right side */}
        <Grid item className={classes.alertMenuRight}>
          <Grid
            container
            direction='column'
            justifyContent='space-between'
            className={classes.alertMenuFullHeight}
          >
            <Grid item>
              <Grid container className='fullHeight' direction='row' justifyContent='space-between'>
                {selectedCategory.alerts
                  .slice((currentPage - 1) * MAX_ALERTS_PER_PAGE, currentPage * MAX_ALERTS_PER_PAGE)
                  .map(
                    (alert, index): JSX.Element => {
                      const buttonTitle = alert.Name ?? '';
                      return (
                        <Grid item className={classes.actionButtonItemContainer} key={index}>
                          <BaseButton
                            style={{ marginBottom: 10 }}
                            buttonType='white'
                            onClick={() => clickAlertHandler(alert.Id)}
                            data-testid='alert-button-action'
                            fullWidth
                          >
                            {buttonTitle}
                          </BaseButton>
                        </Grid>
                      );
                    },
                  )}
              </Grid>
            </Grid>
            <Grid item>
              {/* Bottom Right */}
              <Grid container justifyContent='space-between' alignItems={'center'}>
                {/* bottom right - left side */}
                <Grid item container className={classes.pagination}>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    updateCurrentPageHandler={handlePageChange}
                    testIdPrefix={'alerts-right'}
                  />
                </Grid>

                {/* bottom right - right side */}
                <Grid item>
                  <CloseButton clickHandler={closeClickHandler} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const ItemSpacer = (): JSX.Element => {
    return (
      <Grid item>
        <br />
      </Grid>
    );
  };

  const DialogContent = ({
    categories,
    clickHandler,
    closeClickHandler,
    clickAlertHandler,
  }): JSX.Element => {
    return (
      <Grid container style={{ flex: '1' }} className={classes.alertMenu} direction='column'>
        <ItemSpacer />
        <AlertMenu
          categories={categories}
          clickHandler={clickHandler}
          closeClickHandler={closeClickHandler}
          clickAlertHandler={clickAlertHandler}
        />
        <ItemSpacer />
      </Grid>
    );
  };

  return (
    <EmeraldDialog
      open
      fullWidth={true}
      classes={{
        paper: classes.dialogPaper,
      }}
      data-testid='dialog-alerts'
    >
      <DialogTitle title='Alerts' />
      <DialogContent
        categories={alertCategories}
        clickHandler={onCategoryClick}
        closeClickHandler={onClose}
        clickAlertHandler={onAlertClick}
      />
    </EmeraldDialog>
  );
};

export interface IAlertsState {
  onAlertClick: (alertId: string) => void;
  onClose: () => void;
}

interface IAlertsProps {
  alertCategories: IAlertCategory[];
}

const mapStateToProps = (state: AppState): IAlertsProps => ({
  alertCategories: state.alert,
});

const mapDispatchToProps = {
  onCategoryClick: AlertActions.selectCategory,
};

const AlertsModal = connect(mapStateToProps, mapDispatchToProps)(Alerts);

export default AlertsModal;
